import { createPortal } from "react-dom";
import usePanelFlotante from "./usePanelFlotante";
import "./MenuAcciones.css";

/**
 * El menu de los tres puntos de una fila de tabla.
 *
 * Existe porque las acciones de una fila no caben todas a la vista: la lista
 * de ordenes apilaba cinco botones y estiraba las filas a 180 px, con lo que
 * entraban cinco pedidos en pantalla. Lo que se usa seguido se queda afuera;
 * lo raro y lo destructivo viven aca.
 *
 * Va por portal porque la tabla tiene overflow propio: montado dentro de la
 * fila, el menu se cortaria en el borde de la tabla.
 *
 * Cada opcion es { clave, texto, alElegir, peligrosa?, desactivada?, ayuda? }.
 * Las opciones falsy se descartan, asi quien lo usa puede escribir
 * `permiso && { ... }` sin filtrar antes.
 */
function MenuAcciones({ opciones = [], ayuda = "More actions" }) {
  const items = opciones.filter(Boolean);

  // El alto se mide por la cantidad de opciones y no por el maximo del hook:
  // un menu de tres lineas no tiene por que reservar 260 px.
  const { abierto, setAbierto, triggerRef, panelRef, pos } = usePanelFlotante({
    anclaje: "fin",
    altoLista: Math.max(items.length, 1) * 36,
    extra: 10,
  });

  // Sin opciones no se devuelve null sino un hueco del mismo tamano: en una
  // fila de tabla, una orden ya entregada y pagada no tiene nada que ofrecer
  // aca, y si el boton desaparece los iconos de esa fila se corren 32 px
  // respecto a todas las demas.
  if (items.length === 0) {
    return <span className="menuacc-hueco" aria-hidden="true" />;
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={"menuacc-btn" + (abierto ? " menuacc-btn-on" : "")}
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        title={ayuda}
      >
        {/* Tres puntos dibujados, no el caracter "…": el tipografico se ve
            distinto en cada fuente y a veces queda pegado al borde. */}
        <span className="menuacc-puntos" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </button>

      {abierto && pos
        ? createPortal(
            <div
              ref={panelRef}
              className="menuacc-panel"
              role="menu"
              style={{
                right: pos.right,
                top: pos.top ?? undefined,
                bottom: pos.bottom ?? undefined,
                maxHeight: pos.maxAlto,
              }}
            >
              {items.map((o) => (
                <button
                  key={o.clave}
                  type="button"
                  role="menuitem"
                  className={
                    "menuacc-item" + (o.peligrosa ? " menuacc-item-riesgo" : "")
                  }
                  disabled={o.desactivada}
                  title={o.ayuda}
                  onClick={() => {
                    setAbierto(false);
                    o.alElegir?.();
                  }}
                >
                  {o.texto}
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export default MenuAcciones;
